import { CountBadge } from '@components/CountBadge/CountBadge';
import { MobileFilterDrawer } from '@components/MobileFilterDrawer/MobileFilterDrawer';
import { MobileViewModeButton } from '@components/ViewModeToggle/MobileViewModeButton';
import { useLibrariesViewMode } from '@components/ViewModeToggle/useViewModePreference';
import { countOmniTokens } from '@lib/utils/omniFilter';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { LibrarySortByHash, useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { omniFilterPreferences } from '@stores/OmniFilterPreferences';
import { trackingStore } from '@stores/Tracking';
import { userStore } from '@stores/User';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useCallback, useState } from 'react';
import { debounce } from 'throttle-debounce';
import { MobileCreateLibraryDrawer } from './MobileCreateLibraryDrawer';
import css from './MobileLibrariesOverlay.module.scss';
import { MobileSortDrawer } from './MobileSortDrawer';

export const MobileLibrariesOverlay = observer(function MobileLibrariesOverlay() {
  const libraryManager = useLibraryManager();
  const viewMode = useLibrariesViewMode();

  // Omnifilter state (matches library name or exact library ID)
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filterValue, setFilterValue] = useState<string | null>(libraryManager._omniFilter);

  // Sort drawer state
  const [sortDrawerOpen, setSortDrawerOpen] = useState(false);

  // Create library drawer state
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const trackFilterDebounced = useCallback(
    debounce(500, (query: string | null) => {
      trackingStore.libraryFilterApplied({ query, context: 'libraries' });
    }),
    [],
  );

  const openFilterDrawer = () => {
    // Resync local input with the store in case the filter was cleared elsewhere
    // (e.g. the empty-state "Clear all filters" CTA) while the drawer was closed.
    setFilterValue(libraryManager._omniFilter);
    setFilterDrawerOpen(true);
  };

  const handleFilterChange = (value: string | null) => {
    setFilterValue(value);
    libraryManager.setOmniFilter(value);
    trackFilterDebounced(value);
  };

  const handleFilterApply = () => {
    setFilterDrawerOpen(false);
    libraryManager.setOmniFilter(filterValue);
  };

  const handleFilterClear = () => {
    setFilterValue(null);
    libraryManager.setOmniFilter(null);
    setFilterDrawerOpen(false);
  };

  const handleMatchAllChange = (matchAll: boolean) => {
    omniFilterPreferences.setMatchAll(matchAll);
    trackingStore.libraryFilterMatchModeChanged({ matchAll, context: 'libraries' });
  };

  const handleSortSelect = (value: LibrarySortByHash) => {
    libraryManager.setSorting(value);
    trackingStore.librarySortChanged({ sortBy: value });
  };

  const handleNewLibrary = () => {
    trackingStore.newLibraryButtonClicked();
    setCreateDrawerOpen(true);
  };

  const tokenCount = countOmniTokens(libraryManager._omniFilter);

  return (
    <>
      <div className={css.overlay}>
        <MobileViewModeButton {...viewMode} />

        {/* Filter + Sort group. The whole row is right-aligned — the top-left
            corner belongs to the floating hamburger (MobileNav). */}
        <div className={css.buttonGroup}>
          <button
            className={classNames(css.iconButton, css.filterButton)}
            onClick={openFilterDrawer}
            aria-label="Filter libraries"
          >
            <FilterListRoundedIcon className={css.icon} />
            {tokenCount > 0 && <CountBadge count={tokenCount} />}
          </button>
          <div className={css.divider} />
          <button
            className={css.iconButton}
            onClick={() => setSortDrawerOpen(true)}
            aria-label="Sort libraries"
          >
            <SwapVertIcon className={css.icon} />
          </button>
        </div>

        {/* New library — standalone pill, last */}
        <button
          className={css.newButton}
          onClick={handleNewLibrary}
          disabled={!userStore.data?.isVerified}
          aria-label="Create new library"
        >
          <CreateNewFolderOutlinedIcon className={css.icon} />
        </button>
      </div>

      {/* Name filter drawer */}
      <MobileFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        onOpen={openFilterDrawer}
        placeholder="Name or library ID"
        value={filterValue}
        onChange={handleFilterChange}
        onApply={handleFilterApply}
        onClear={handleFilterClear}
        matchAll={omniFilterPreferences.matchAll}
        onMatchAllChange={handleMatchAllChange}
      />

      {/* Sort drawer */}
      <MobileSortDrawer
        open={sortDrawerOpen}
        onClose={() => setSortDrawerOpen(false)}
        onOpen={() => setSortDrawerOpen(true)}
        selectedSort={libraryManager.sortByHash}
        onSelect={handleSortSelect}
      />

      {/* Create library drawer */}
      <MobileCreateLibraryDrawer
        open={createDrawerOpen}
        onClose={() => setCreateDrawerOpen(false)}
        onOpen={() => setCreateDrawerOpen(true)}
      />
    </>
  );
});
